import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Camera, Upload, X, Sparkles, Loader2, Mic, ScanBarcode, Clock, Store, Type, ArrowLeft } from 'lucide-react';

export function MealTypeSelector({ mealType, setMealType }) {
  const navigate = useNavigate();
  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 20, padding: '0 16px' }}>
      {['breakfast', 'lunch', 'dinner', 'snack'].map(t => (
        <button key={t} data-testid={`meal-type-${t}`} onClick={() => setMealType(t)} style={{
          minWidth: 72, height: 36, padding: '0 14px', borderRadius: 18, fontSize: 13,
          fontFamily: 'var(--font-sans)', cursor: 'pointer', fontWeight: 500, textTransform: 'capitalize',
          border: mealType === t ? 'none' : '1px solid #E8E0D8',
          background: mealType === t ? '#C67A5C' : '#FFFFFF',
          color: mealType === t ? '#FFFFFF' : '#9A8F87',
          transition: 'all 0.15s',
        }}>{t}</button>
      ))}
      <button data-testid="meal-type-beverage" onClick={() => navigate('/beverages')} style={{
        minWidth: 72, height: 36, padding: '0 14px', borderRadius: 18, fontSize: 13,
        fontFamily: 'var(--font-sans)', cursor: 'pointer', fontWeight: 500,
        border: '1px solid #E8E0D8', background: '#FFFFFF', color: '#9A8F87',
        transition: 'all 0.15s',
      }}>Beverage</button>
    </div>
  );
}

export function CapturePhotoView({
  mealType, setMealType, imagePreview, imageFile, fileInputRef, textInput, setTextInput,
  textAnalyzing, textInputRef, handleFileSelect, handleTextSubmit, handleAnalyze, error,
  setImagePreview, setImageFile, setError, setActiveTab, navigate,
}) {
  return (
    <div className="nuri-reveal nuri-d0">
      <MealTypeSelector mealType={mealType} setMealType={setMealType} />
      <p style={{ fontSize: 13, margin: '0 0 16px', color: '#9A8F87', fontFamily: 'var(--font-sans)', textAlign: 'center' }}>
        Snap a photo — Nuri handles the rest
      </p>

      {!imagePreview ? (
        <div data-testid="upload-zone" onClick={() => fileInputRef.current?.click()}
          style={{ borderRadius: 24, marginBottom: 24, border: '2px dashed #E8E3DB', cursor: 'pointer', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', opacity: 0.12 }}>
            <img src="/food-images/avocado_toast.png" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <img src="/food-images/salmon_fillet.png" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <img src="/food-images/smoothie_bowl.png" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <div style={{ position: 'relative', zIndex: 1, padding: '48px 24px' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#C67A5C', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
              <Camera className="w-8 h-8 text-white" />
            </div>
            <p style={{ fontSize: 16, margin: '0 0 6px', fontWeight: 500, color: '#1A1A18', fontFamily: 'var(--font-sans)' }}>Take a photo or upload</p>
            <p style={{ fontSize: 13, margin: 0, color: '#9A9590', fontFamily: 'var(--font-sans)' }}>AI analyzes every ingredient in under 3 seconds</p>
          </div>
          {/* Barcode quick-switch button on upload zone */}
          <button data-testid="camera-barcode-btn" onClick={(e) => { e.stopPropagation(); setActiveTab('barcode'); }}
            style={{ position: 'absolute', bottom: 12, left: 12, zIndex: 2, display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 20, background: 'rgba(26,58,74,0.85)', backdropFilter: 'blur(8px)',
              color: '#F0EDE8', fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-sans)', border: 'none', cursor: 'pointer',
              transition: 'all 0.15s' }}>
            <ScanBarcode className="w-4 h-4" /> Scan Barcode
          </button>
        </div>
      ) : (
        <div className="relative" style={{ marginBottom: 24 }}>
          <img src={imagePreview} alt="Meal" style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderRadius: 24 }} />
          <button data-testid="clear-image" onClick={() => { setImagePreview(null); setImageFile(null); setError(''); }}
            style={{ position: 'absolute', top: 12, right: 12, width: 40, height: 40, borderRadius: '50%', background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <X className="w-5 h-5" />
          </button>
          {/* Barcode quick-switch on image preview */}
          <button data-testid="preview-barcode-btn" onClick={() => { setImagePreview(null); setImageFile(null); setActiveTab('barcode'); }}
            style={{ position: 'absolute', bottom: 12, left: 12, display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 20, background: 'rgba(26,58,74,0.85)', backdropFilter: 'blur(8px)',
              color: '#F0EDE8', fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-sans)', border: 'none', cursor: 'pointer' }}>
            <ScanBarcode className="w-4 h-4" /> Scan Barcode Instead
          </button>
        </div>
      )}

      <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} className="hidden" />

      <div data-testid="text-meal-input-container" style={{ display: 'flex', gap: 8, marginBottom: 20, background: '#FFFFFF', borderRadius: 16, padding: 6, border: '1px solid #E8E3DB' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Type className="w-4 h-4" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9A9590' }} />
          <input ref={textInputRef} data-testid="text-meal-input" type="text" value={textInput}
            onChange={e => setTextInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleTextSubmit()}
            placeholder="Or just type what you ate..." disabled={textAnalyzing}
            style={{ width: '100%', padding: '12px 12px 12px 38px', border: 'none', background: 'transparent', fontSize: 14, color: '#1A1A18', fontFamily: 'var(--font-sans)', outline: 'none', opacity: textAnalyzing ? 0.5 : 1 }} />
        </div>
        <button data-testid="text-meal-submit" onClick={handleTextSubmit} disabled={!textInput.trim() || textAnalyzing}
          style={{ padding: '10px 18px', borderRadius: 12, border: 'none', cursor: 'pointer', background: textInput.trim() ? '#5A8C6A' : '#E8E3DB', color: textInput.trim() ? '#FFF' : '#9A9590', fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-sans)', display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.2s', whiteSpace: 'nowrap' }}>
          {textAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {textAnalyzing ? 'Analyzing...' : 'Log it'}
        </button>
      </div>

      {error && (
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <p style={{ color: '#C45B5B', fontSize: 14, marginBottom: 12 }}>{error}</p>
          <Button data-testid="retry-btn" onClick={handleAnalyze}
            style={{ background: '#5A8C6A', color: '#fff', borderRadius: 12, padding: '10px 24px', border: 'none', cursor: 'pointer' }}>
            <Sparkles className="w-4 h-4 mr-2" /> Try Again
          </Button>
        </div>
      )}

      <p style={{ fontSize: 11, letterSpacing: '0.8px', color: '#9A9590', fontFamily: 'var(--font-sans)', textTransform: 'uppercase', margin: '0 0 12px', fontWeight: 500 }}>Other ways to log</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, textAlign: 'left', marginBottom: 16 }}>
        {[
          { icon: Mic, name: 'Voice', sub: 'Say what you ate', tab: 'voice' },
          { icon: ScanBarcode, name: 'Barcode', sub: 'Scan packaged food', tab: 'barcode' },
          { icon: Clock, name: 'Quick re-log', sub: 'From your history', path: '/history' },
          { icon: Store, name: 'Restaurant', sub: 'Search menus', path: '/restaurants' },
        ].map((m) => (
          <button key={m.name} data-testid={`alt-log-${m.name.toLowerCase().replace(/\s+/g,'-')}`}
            onClick={() => m.path ? navigate(m.path) : setActiveTab(m.tab)}
            style={{ background: '#FFFFFF', border: '1px solid #E8E3DB', borderRadius: 14, padding: 14, textAlign: 'left', cursor: 'pointer' }}>
            <m.icon style={{ width: 22, height: 22, color: '#C67A5C', marginBottom: 6 }} />
            <p style={{ fontSize: 13, margin: 0, fontWeight: 500, color: '#1A1A18', fontFamily: 'var(--font-sans)' }}>{m.name}</p>
            <p style={{ fontSize: 11, margin: '2px 0 0', color: '#9A9590', fontFamily: 'var(--font-sans)' }}>{m.sub}</p>
          </button>
        ))}
      </div>

      <button data-testid="batch-upload-btn" onClick={() => navigate('/batch-upload')} style={{
        width: '100%', padding: 16, borderRadius: 16, background: '#F5F0E8', border: '1px solid #E8E3DB', cursor: 'pointer', textAlign: 'left', display: 'flex', gap: 14, alignItems: 'center',
      }}>
        <Upload style={{ width: 22, height: 22, color: '#5A8C6A' }} />
        <div>
          <p style={{ fontSize: 14, margin: 0, fontWeight: 500, color: '#1A1A18', fontFamily: 'var(--font-sans)' }}>Log my whole day</p>
          <p style={{ fontSize: 12, margin: '2px 0 0', color: '#9A9590', fontFamily: 'var(--font-sans)' }}>Upload all your food photos at once</p>
        </div>
      </button>
    </div>
  );
}

export function SubViewWrapper({ onBack, children }) {
  return (
    <div>
      <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0', marginBottom: 16, background: 'none', border: 'none', cursor: 'pointer', color: '#5A8C6A', fontSize: 13, fontFamily: 'var(--font-sans)' }}>
        <ArrowLeft className="w-4 h-4" /> Back to camera
      </button>
      {children}
    </div>
  );
}
